import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Icon from './icons/Icon';
import { createTokyoDateTime, getTokyoDateParts } from '../utils/dateUtils';
import './InputTime.css';

type InputTimeVariant = 'default' | 'ghost';

interface InputTimeProps {
  /** 見た目のバリエーション */
  variant?: InputTimeVariant;
  /** デフォルト値（現地時間のHH:MM形式またはDateオブジェクト） */
  defaultValue?: string | Date;
  /** 値（制御されたコンポーネント用） */
  value?: string | Date;
  /** 変更時のコールバック（現地時間のISO文字列） */
  onChange?: (value: string | null) => void;
  /** フォーカス時のコールバック */
  onFocus?: () => void;
  /** ブラー時のコールバック */
  onBlur?: () => void;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー状態 */
  error?: boolean;
  /** 必須項目 */
  required?: boolean;
  /** 名前属性 */
  name?: string;
  /** ID属性 */
  id?: string;
  /** 24時間形式（デフォルト：true） */
  format24?: boolean;
  /** 追加のクラス名 */
  className?: string;
  /** aria-label */
  'aria-label'?: string;
  /** aria-describedby */
  'aria-describedby'?: string;
}

interface InputTimeRef {
  focus: () => void;
  blur: () => void;
  getValue: () => string | null;
  setValue: (value: string | Date | null) => void;
}

// 時間文字列をDateオブジェクトに変換（今日の日付で）
const timeStringToDate = (timeString: string): Date | null => {
  if (!timeString || typeof timeString !== 'string') return null;
  
  const match = timeString.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  
  const today = getTokyoDateParts(new Date());
  return createTokyoDateTime({ ...today, hour: hours, minute: minutes, second: 0 });
};

// DateオブジェクトまたはISO文字列からHH:MM形式に変換
const dateToTimeString = (date: Date | string | null): string => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const { hour, minute } = getTokyoDateParts(dateObj);
  const hours = hour.toString().padStart(2, '0');
  const minutes = minute.toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const InputTime = forwardRef<InputTimeRef, InputTimeProps>(({
  variant = 'default',
  defaultValue,
  value,
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  error = false,
  required = false,
  name,
  id,
  format24 = true,
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}, ref) => {
  // 時間の管理（現地時間）
  const [internalTime, setInternalTime] = useState<Date | null>(() => {
    const initialValue = value || defaultValue;
    if (initialValue instanceof Date) return initialValue;
    if (typeof initialValue === 'string') {
      if (initialValue.match(/^\d{1,2}:\d{2}$/)) {
        // HH:MM形式の時間文字列
        return timeStringToDate(initialValue);
      } else {
        // ISO文字列またはその他の日付文字列
        return new Date(initialValue);
      }
    }
    return null;
  });

  // フォーカス状態の管理
  const [isFocused, setIsFocused] = useState(false);
  
  // 各フィールドの参照
  const hoursInputRef = useRef<HTMLInputElement>(null);
  const minutesInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 制御されたコンポーネントとしての値更新
  useEffect(() => {
    if (value !== undefined) {
      let newTime: Date | null = null;
      if (value instanceof Date) {
        newTime = value;
      } else if (typeof value === 'string') {
        if (value.match(/^\d{1,2}:\d{2}$/)) {
          // HH:MM形式の時間文字列
          newTime = timeStringToDate(value);
        } else if (value) {
          // ISO文字列またはその他の日付文字列
          newTime = new Date(value);
        }
      }
      setInternalTime(newTime);
    }
  }, [value]);

  // refの公開
  useImperativeHandle(ref, () => ({
    focus: () => {
      hoursInputRef.current?.focus();
    },
    blur: () => {
      const activeElement = document.activeElement;
      if (activeElement === hoursInputRef.current || 
          activeElement === minutesInputRef.current) {
        (activeElement as HTMLElement).blur();
      }
    },
    getValue: () => {
      return internalTime ? internalTime.toISOString() : null;
    },
    setValue: (newValue) => {
      let newTime: Date | null = null;
      if (newValue instanceof Date) {
        newTime = newValue;
      } else if (typeof newValue === 'string') {
        if (newValue.includes(':')) {
          newTime = timeStringToDate(newValue);
        } else if (newValue) {
          newTime = new Date(newValue);
        }
      }
      setInternalTime(newTime);
      if (onChange) {
        onChange(newTime ? newTime.toISOString() : null);
      }
    }
  }));

  // 時間の更新ハンドラー
  const updateTime = (hours?: number, minutes?: number) => {
    const current = getTokyoDateParts(internalTime || new Date());
    const newHours = hours !== undefined ? hours : current.hour;
    const newMinutes = minutes !== undefined ? minutes : current.minute;
    
    // 有効な時間かチェック
    if (newHours >= 0 && newHours <= 23 && newMinutes >= 0 && newMinutes <= 59) {
      const today = getTokyoDateParts(new Date());
      const newTime = createTokyoDateTime({ ...today, hour: newHours, minute: newMinutes, second: 0 });
      setInternalTime(newTime);
      if (onChange) {
        onChange(newTime.toISOString());
      }
    }
  };

  // 時間の変更ハンドラー
  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hours = parseInt(e.target.value);
    if (!isNaN(hours) && hours >= 0 && hours <= 23) {
      updateTime(hours);
    }
  };

  // 分の変更ハンドラー
  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const minutes = parseInt(e.target.value);
    if (!isNaN(minutes) && minutes >= 0 && minutes <= 59) {
      updateTime(undefined, minutes);
    }
  };

  // キーボードナビゲーション
  const handleKeyDown = (e: React.KeyboardEvent, field: 'hours' | 'minutes') => {
    const current = getTokyoDateParts(internalTime || new Date());
    let hours = current.hour;
    let minutes = current.minute;
    // Shiftを押している間は、時・分ともに10単位で変更する
    const step = e.shiftKey ? 10 : 1;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (field === 'hours') {
        updateTime((hours + step) % 24);
      } else if (field === 'minutes') {
        updateTime(hours, (minutes + step) % 60);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (field === 'hours') {
        updateTime((hours - step + 24) % 24);
      } else if (field === 'minutes') {
        updateTime(hours, (minutes - step + 60) % 60);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (field === 'minutes') {
        hoursInputRef.current?.focus();
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (field === 'hours') {
        minutesInputRef.current?.focus();
      }
    } else if (e.key === 'Tab') {
      // Tabナビゲーションは標準動作を維持
    }
  };

  // フォーカス管理
  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) onFocus();
  };

  const handleBlur = (e: React.FocusEvent) => {
    // 関連要素がコンテナ内かチェック
    const relatedTarget = e.relatedTarget as Element;
    if (!containerRef.current?.contains(relatedTarget)) {
      setIsFocused(false);
      if (onBlur) onBlur();
    }
  };

  // 時間の表示値
  const displayParts = internalTime ? getTokyoDateParts(internalTime) : null;
  const displayHours = displayParts ? displayParts.hour.toString().padStart(2, '0') : '';
  const displayMinutes = displayParts ? displayParts.minute.toString().padStart(2, '0') : '';

  return (
    <div 
      ref={containerRef}
      className={`input-time input-time--${variant} ${error ? 'input-time--error' : ''} ${disabled ? 'input-time--disabled' : ''} ${isFocused ? 'input-time--focused' : ''} ${className}`}
      onBlur={handleBlur}
    >
      <div className="input-time__state-layer">
        <div className="input-time__container">
          {/* 時計アイコン */}
          <div className="input-time__icon">
            <Icon name="time" size={24} />
          </div>

          {/* 時間入力フィールド */}
          <div className="input-time__fields">
            {/* 時間 */}
            <input
              ref={hoursInputRef}
              type="number"
              className="input-time__field input-time__field--hours text-single-large"
              value={displayHours}
              onChange={handleHoursChange}
              onKeyDown={(e) => handleKeyDown(e, 'hours')}
              onFocus={handleFocus}
              disabled={disabled}
              min="0"
              max="23"
              placeholder="HH"
              aria-label="時間"
              name={name ? `${name}_hours` : undefined}
              id={id ? `${id}_hours` : undefined}
            />
            
            <span className="input-time__separator">:</span>
            
            {/* 分 */}
            <input
              ref={minutesInputRef}
              type="number"
              className="input-time__field input-time__field--minutes text-single-large"
              value={displayMinutes}
              onChange={handleMinutesChange}
              onKeyDown={(e) => handleKeyDown(e, 'minutes')}
              onFocus={handleFocus}
              disabled={disabled}
              min="0"
              max="59"
              placeholder="MM"
              aria-label="分"
              name={name ? `${name}_minutes` : undefined}
              id={id ? `${id}_minutes` : undefined}
            />
          </div>
        </div>
      </div>

      {/* 隠しフィールド（フォーム送信用） */}
      <input
        type="hidden"
        name={name}
        value={internalTime ? internalTime.toISOString() : ''}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error}
      />
    </div>
  );
});

InputTime.displayName = 'InputTime';

export default InputTime;
export type { InputTimeRef };
